import React, { useState, useCallback, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert } from "@mui/material";
import Cropper from "react-easy-crop";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });

/**
 * Returns a blob for the cropped region (bounding box of the circle).
 * @param {string} imageSrc - Data URL or object URL of the image
 * @param {{ x: number, y: number, width: number, height: number }} pixelCrop
 * @param {string} [mimeType] - e.g. "image/jpeg"
 * @returns {Promise<Blob>}
 */
export async function getCroppedImg(imageSrc, pixelCrop, mimeType = "image/jpeg") {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))),
      mimeType,
      0.92,
    );
  });
}

const HeadshotCropModal = ({ open, imageFile, onApply, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [applying, setApplying] = useState(false);
  const [cropError, setCropError] = useState(null);
  const croppedAreaPixelsRef = useRef(null);
  const imageUrl = imageFile ? URL.createObjectURL(imageFile) : null;

  const handleCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
    croppedAreaPixelsRef.current = croppedAreaPixels;
  }, []);

  const handleApply = useCallback(async () => {
    if (!imageUrl || !croppedAreaPixelsRef.current || !imageFile) return;
    setCropError(null);
    setApplying(true);
    try {
      const mime = imageFile.type && imageFile.type.startsWith("image/") ? imageFile.type : "image/jpeg";
      const blob = await getCroppedImg(imageUrl, croppedAreaPixelsRef.current, mime);
      const file = new File([blob], imageFile.name, { type: blob.type });
      onApply(file);
      onCancel();
    } catch (err) {
      setCropError(err?.message || "Crop failed. Please try again.");
    } finally {
      setApplying(false);
    }
  }, [imageUrl, imageFile, onApply, onCancel]);

  const handleCancel = useCallback(() => {
    croppedAreaPixelsRef.current = null;
    setCropError(null);
    onCancel();
  }, [onCancel]);

  React.useEffect(() => {
    if (!open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropError(null);
      croppedAreaPixelsRef.current = null;
    }
  }, [open]);

  React.useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  if (!imageFile) return null;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Crop headshot</DialogTitle>
      <DialogContent>
        {cropError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCropError(null)}>
            {cropError}
          </Alert>
        )}
        <Typography component="p" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag to position and use the slider to zoom. The circle is what will be saved.
        </Typography>
        <Box sx={{ position: "relative", height: 360, width: "100%", bgcolor: "grey.900" }}>
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={handleCropComplete}
              onZoomChange={setZoom}
            />
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography component="label" variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Zoom
          </Typography>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: "100%", accentColor: "var(--mui-palette-primary-main)" }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={applying}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={applying}>
          {applying ? "Applying…" : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HeadshotCropModal;
